/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Index';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { getPrivacyPage } from '@/sanity/client';
import { format } from 'date-fns';
import { PortableText } from '@portabletext/react';

const PrivacyHeader = ({ header }: { header: any }) => {
	// Background image URL - use CMS data if available, otherwise fallback
	const backgroundImageUrl = header?.backgroundImage
		? header.backgroundImage
		: '/contact-banner.svg';

	// Overlay opacity - use CMS data if available, otherwise fallback to 50%
	const overlayOpacity =
		header?.overlayOpacity !== undefined ? header.overlayOpacity / 100 : 0.5;

	return (
		<section className=''>
			<div
				className='relative flex flex-col items-center px-16 pt-20 w-full bg max-md:px-5 max-md:max-w-full'
				style={{
					backgroundImage: `url(${backgroundImageUrl})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
				}}
			>
				<div
					className='absolute inset-0 bg-black'
					style={{ opacity: overlayOpacity }}
				></div>
				<div className='relative z-10 mb-0 w-full max-w-[1215px] max-md:mb-2.5 max-md:max-w-full'>
					<div className='flex gap-5 max-md:flex-col py-4 md:px-10 px-5'>
						<div className='w-[65%] max-md:ml-0 max-md:w-full py-8'>
							<div className='mr-0 text-neutral-100 max-md:max-w-full'>
								<h1 className='text-5xl leading-[80px] max-md:max-w-full max-md:text-4xl max-md:leading-10'>
									{header?.title || 'Privacy Policy'}
								</h1>
								<div className='mt-4 max-w-xl'>
									<p className='mt-6 text-3xl leading-10 max-md:max-w-full'>
										{header?.subtitle ||
											'How we collect, use, and protect your personal information'}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

const Privacy = () => {
	const [privacyData, setPrivacyData] = useState<any>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchPrivacyData = async () => {
			try {
				const pageData = await getPrivacyPage();
				if (pageData) {
					setPrivacyData(pageData);
				}
			} catch (error) {
				console.error('Error fetching privacy page data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchPrivacyData();
	}, []);

	// Default fallback policies in case CMS data isn't available
	const defaultPolicies = [
		{
			title: '1. Information We Collect',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'We may collect personal information that you provide directly to us, such as your name, email address, and any other information you choose to provide when using our services or contacting us.',
						},
					],
				},
			],
		},
		{
			title: '2. How We Use Your Information',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'We use the information we collect to provide, maintain, and improve our services, communicate with you, and for other legitimate business purposes.',
						},
					],
				},
			],
		},
		{
			title: '3. Information Sharing',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'We do not sell or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business.',
						},
					],
				},
			],
		},
		{
			title: '4. Cookies and Tracking Technologies',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'Our website may use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.',
						},
					],
				},
			],
		},
		{
			title: '5. Data Security',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'We implement appropriate security measures to protect your personal information. However, no method of transmission or electronic storage is 100% secure, and we cannot guarantee absolute security.',
						},
					],
				},
			],
		},
		{
			title: '6. Your Rights',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'You may have rights regarding your personal information, including the right to access, correct, or delete the personal information we hold about you. To exercise these rights, please contact us.',
						},
					],
				},
			],
		},
		{
			title: '7. Changes to This Privacy Policy',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.',
						},
					],
				},
			],
		},
		{
			title: '8. Contact Us',
			content: [
				{
					_type: 'block',
					children: [
						{
							_type: 'span',
							text: 'If you have any questions about this Privacy Policy, please contact us at info@yaap.co.za.',
						},
					],
				},
			],
		},
	];

	// Use CMS policies if available, otherwise use fallback
	const policies = privacyData?.policies || defaultPolicies;
	const lastUpdated = privacyData?.lastUpdated
		? format(new Date(privacyData.lastUpdated), 'MMMM yyyy')
		: '17 March 2025';

	if (loading) {
		return (
			<Layout>
				<div className='flex justify-center items-center min-h-screen'>
					<p>Loading privacy policy...</p>
				</div>
			</Layout>
		);
	}

	return (
		<div className='flex flex-col w-full overflow-hidden'>
			<Layout>
				<main className='w-full'>
					<PrivacyHeader header={privacyData?.header} />

					<section className='py-12 md:px-34 bg-background rounded-t-3xl relative z-20 -mt-5'>
						<div className='max-w-7xl md:px-15 px-10'>
							<div className='mb-10'>
								<h2 className='text-2xl font-semibold mb-6'>
									Our Privacy Policy
								</h2>
								<p className='text-muted-foreground mb-8'>
									{privacyData?.introduction ||
										`Last Updated: ${lastUpdated}. This privacy policy describes how we collect, use, and handle your personal information when you use our website and services.`}
								</p>
							</div>

							<Accordion type='single' collapsible className='w-full'>
								{policies.map((policy: any, index: number) => (
									<AccordionItem
										key={index}
										value={`item-${index}`}
										className='border-b'
									>
										<AccordionTrigger className='text-left font-medium'>
											{policy.title}
										</AccordionTrigger>
										<AccordionContent className='text-muted-foreground'>
											{Array.isArray(policy.content) ? (
												<div className='py-2'>
													<PortableText value={policy.content} />
												</div>
											) : (
												<p className='py-2'>{policy.content}</p>
											)}
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>

							<div className='mt-12 pt-6 border-t border-gray-100'>
								<p className='text-sm text-muted-foreground'>
									{privacyData?.footer ||
										'By using our website and services, you consent to the privacy practices described in this policy.'}
								</p>
							</div>
						</div>
					</section>
				</main>
			</Layout>
		</div>
	);
};

export default Privacy;
